import { combine, declareAction, declareAtom, map } from "@helium10/re-core";
export const initialState = {
    value: null,
    options: [],
    disabled: false,
    isOpen: false,
    highlightedIndex: 0,
    onChange: () => void 0,
    autocomplete: false,
    searchString: "",
    notFilter: false,
    onSearch: () => void 0,
    cacheSelectedOptions: {},
};
export const setStateAction = declareAction("select/setStateAction");
export const setCacheAction = declareAction("select/setCacheAction");
export const toggleOpenAction = declareAction("select/toggleOpenAction");
export const setHighlightAction = declareAction("select/setHighlight", ({ index, callback }, store) => {
    const state = store.getState(selectAtom);
    const { list } = store.getState(selectOptionsSelector);
    if (typeof index === "number") {
        if (index !== state.highlightedIndex) {
            store.dispatch(privateSetHighlightAction({
                index,
                callback,
            }));
        }
    }
    else {
        let newIndex = list[0]?.index || 0;
        const currentIndex = list.findIndex((opt) => opt.index === state.highlightedIndex);
        if (index === "next" && currentIndex !== list.length - 1 && list[currentIndex + 1]) {
            newIndex = list[currentIndex + 1].index;
        }
        if (index === "prev" && currentIndex !== list[0]?.index && list[currentIndex - 1]) {
            newIndex = list[currentIndex - 1].index;
        }
        if (newIndex !== state.highlightedIndex) {
            store.dispatch(privateSetHighlightAction({
                index: newIndex,
                callback,
            }));
        }
    }
});
const privateSetHighlightAction = declareAction("select/setPrivateHighlight", (props) => {
    props.callback?.(props.index);
});
export const clearOptionValue = "__clear__";
export const setValueAction = declareAction("select/setValue", (opt, store) => {
    const state = store.getState(selectAtom);
    const isMulti = store.getState(selectIsMultiSelector);
    if (opt === "_all") {
        const all = store.getState(selectOptionsSelector).list;
        const filtered = all.filter((opt) => !opt.disabled);
        const newValue = filtered.map((opt) => opt.value);
        state.onChange?.(newValue);
    }
    else if (opt === "_clear" || opt.value === clearOptionValue) {
        const newValue = isMulti ? [] : null;
        state.onChange?.(newValue);
    }
    else {
        let returnValue;
        if (isMulti) {
            const multiValue = state.value;
            returnValue = opt.active
                ? multiValue.filter((p) => p !== opt.value)
                : [...multiValue, opt.value];
        }
        else {
            returnValue = opt.value;
        }
        state.onChange?.(returnValue);
    }
});
export const setSearchStringAction = declareAction("select/setSearchString", (text, { getState }) => {
    if (text) {
        getState(selectAtom).onSearch?.(text, getState(selectSelectedSelector));
    }
});
export const selectAtom = declareAtom("selectAtom", initialState, (on) => [
    on(setStateAction, (state, newState) => ({ ...state, ...newState })),
    on(setCacheAction, (state, cacheSelectedOptions) => ({ ...state, cacheSelectedOptions })),
    on(toggleOpenAction, (state, isOpen) => ({
        ...state,
        isOpen: state.disabled ? false : isOpen ?? !state.isOpen,
    })),
    on(privateSetHighlightAction, (state, { index: highlightedIndex }) => ({
        ...state,
        highlightedIndex,
    })),
    on(setSearchStringAction, (state, searchString) => ({ ...state, searchString })),
]);
export const selectOpenSelector = map("open", selectAtom, (state) => state.isOpen);
export const selectIsMultiSelector = map("isMulti", selectAtom, (state) => {
    return Array.isArray(state.value);
});
export const selectGroupedSelector = map("grouped", selectAtom, (state) => {
    return (state.options?.length || 0) === 0 && !!state.groupOptions;
});
export const optionIsActive = ({ value, isMulti, optionValue, }) => {
    return isMulti ? value.includes(optionValue) : value === optionValue;
};
const getExtendedOption = (data) => {
    const active = optionIsActive({
        isMulti: data.isMulti,
        optionValue: data.option.value,
        value: data.value,
    });
    return {
        ...data.option,
        searchString: data.option.searchString || "",
        key: (data.group || "wog") + "_" + data.option.value.toString(),
        disabled: data.option.disabled || false,
        active,
        hovered: data.index === data.highlightedIndex,
        index: data.index,
        multiple: data.isMulti,
        group: data.group?.id,
        isGroupFirstOption: data.group?.options[0]?.value === data.option.value,
    };
};
export const selectOptionsSelector = map("options", combine([selectAtom, selectIsMultiSelector, selectGroupedSelector]), ([state, isMulti, grouped]) => {
    const { value, highlightedIndex, options: _options = [], groupOptions = [], hideSelected, clearable, } = state;
    const cached = Object.values(state.cacheSelectedOptions).filter((cachedOption) => !_options.find((option) => option.value === cachedOption.value));
    const clearableOption = {
        label: "None",
        value: clearOptionValue,
    };
    const hasValue = isMulti ? value.length > 0 : Boolean(value);
    const options = clearable && hasValue ? [clearableOption, ...cached, ..._options] : [...cached, ..._options];
    const map = {};
    const groupMap = {};
    const list = [];
    if (grouped) {
        let index = cached.length > 0 ? cached.length - 1 : 0;
        groupOptions.forEach((group) => {
            for (const option of group.options) {
                const extended = getExtendedOption({
                    value,
                    isMulti,
                    option,
                    index,
                    highlightedIndex,
                    group,
                });
                index++;
                if (!hideSelected) {
                    list.push(extended);
                }
                else if (!extended.active) {
                    list.push(extended);
                }
                map[extended.value] = extended;
                groupMap[group.id] = group;
            }
        });
    }
    else {
        for (const [index, option] of options.entries()) {
            const extended = getExtendedOption({
                value,
                isMulti,
                option,
                index,
                highlightedIndex,
            });
            if (!hideSelected) {
                list.push(extended);
            }
            else if (!extended.active) {
                list.push(extended);
            }
            map[extended.value] = extended;
        }
    }
    return { list, map, groupMap };
});
const hasValue = (value) => value !== null && value !== undefined;
export const selectSelectedSelector = map("valueOptions", combine([selectAtom, selectOptionsSelector, selectIsMultiSelector]), ([{ value }, { map }, isMulti]) => {
    if (isMulti) {
        const res = value.map((val) => map[val]);
        return res.filter((val) => Boolean(val));
    }
    return hasValue(map[value]) ? [map[value]] : [];
});
export const selectCommonSelector = map(combine([
    selectAtom,
    selectIsMultiSelector,
    selectGroupedSelector,
    selectOptionsSelector,
    selectSelectedSelector,
]), ([state, isMulti, grouped, options, selected]) => {
    return {
        value: state.value,
        isMulti,
        options,
        selected,
        highlighted: options.list.find((opt) => opt.index === state.highlightedIndex),
        disabled: !!state.disabled,
        grouped,
        isOpen: state.isOpen,
        autocomplete: state.autocomplete,
        placeholder: state.placeholder,
        notFilter: state.notFilter,
    };
});
export const selectSearchStringSelector = map(selectAtom, (state) => state.searchString);
